import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  IconButton,
  Tooltip,
  Button,
  Stack,
} from '@mui/material';
import {
  FolderOpen as ProjectsIcon,
  Description as TemplatesIcon,
  FolderSpecial as SpacesIcon,
  Info as InfoIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

export const NAV_HEIGHT = 48;
export const DRAWER_WIDTH = 280;
export const DRAWER_WIDTH_COLLAPSED = 64;

interface SidebarProps {
  open?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

// Custom icon wrapper with circle background - hover effect only on icon
const CircleIcon: React.FC<{ icon: React.ReactNode; selected: boolean }> = ({ icon, selected }) => (
  <Box
    className="circle-icon"
    sx={{
      width: 32,
      height: 32,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: selected ? 'var(--brand-primary)' : 'transparent',
      color: selected ? '#FFFFFF' : 'var(--brand-primary)',
      transition: 'all 0.2s ease',
      mr: 1.5,
      '& .MuiSvgIcon-root': {
        fontSize: '1.25rem',
      },
    }}
  >
    {icon}
  </Box>
);

export const Sidebar: React.FC<SidebarProps> = ({ 
  open = true, 
  collapsed = false,
  onToggleCollapse,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { text: 'Projects', icon: <ProjectsIcon />, path: '/projects' },
    { text: 'Templates', icon: <TemplatesIcon />, path: '/templates' },
    { text: 'Documents', icon: <SpacesIcon />, path: '/spaces' },
    { text: 'About', icon: <InfoIcon />, path: '/about' },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const isSelected = (path: string) => {
    if (path === '/projects') {
      return location.pathname === '/projects' || location.pathname.startsWith('/projects/');
    }
    if (path === '/spaces') {
      return location.pathname === '/spaces' || location.pathname.startsWith('/spaces/');
    }
    return location.pathname === path;
  };

  const currentWidth = collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          top: NAV_HEIGHT,
          height: `calc(100% - ${NAV_HEIGHT}px)`,
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
        },
      }}
    >
      {/* Collapse Toggle Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: collapsed ? 'center' : 'flex-end',
        p: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}>
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton 
            size="small" 
            onClick={onToggleCollapse}
            sx={{
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Navigation Buttons */}
      <Stack 
        spacing={1} 
        sx={{ 
          p: collapsed ? 1 : 2, 
          pt: 2,
          alignItems: collapsed ? 'center' : 'stretch',
        }}
      >
        {menuItems.map((item) => {
          const selected = isSelected(item.path);
          
          return (
            <Tooltip key={item.text} title={collapsed ? item.text : ''} placement="right">
              {collapsed ? (
                <Button
                  variant={selected ? 'contained' : 'outlined'}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    minWidth: 48,
                    width: 48,
                    height: 48,
                    p: 0,
                    borderRadius: '12px',
                  }}
                >
                  {item.icon}
                </Button>
              ) : (
                <Button
                  variant="text"
                  onClick={() => handleNavigation(item.path)}
                  disableRipple
                  sx={{
                    justifyContent: 'flex-start',
                    width: '100%',
                    py: 0.5,
                    px: 1,
                    borderRadius: '100px',
                    backgroundColor: 'transparent !important',
                    color: 'var(--brand-primary)',
                    fontWeight: selected ? 500 : 400,
                    fontSize: '0.9375rem',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'transparent !important',
                      '& .circle-icon': {
                        backgroundColor: selected ? 'var(--brand-primary)' : 'rgba(0, 0, 0, 0.08)',
                        boxShadow: selected ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                      },
                    },
                    '&:active': {
                      backgroundColor: 'transparent !important',
                    },
                    '&.Mui-focusVisible': {
                      backgroundColor: 'transparent !important',
                    },
                  }}
                >
                  <CircleIcon icon={item.icon} selected={selected} />
                  {item.text}
                </Button>
              )}
            </Tooltip>
          );
        })}
      </Stack>
    </Drawer>
  );
};
